import {
    AssertionDescriptorUsageTypesEnum,
    CaseFileStaticItems,
} from '../../../utilities/Constants';
import { IAlertListModel, ICaseTriggerFieldModel } from '../../../interfaces/INote';
import { IQueryFilterCriteria, ITriggerRuleField } from '../../../interfaces/IQuery';

import { CaseName } from '../../../components/case/CaseName';
import Common from '../../../stores/Common';
import { ICaseTitleModel } from '../../../interfaces/ICase';
import { ITriggerRuleSettingsModel } from '../../../interfaces/ITriggerRule';
import { IUserModel } from '../../../interfaces/IUser';
import { Modal } from 'react-bootstrap';
import TriggerRuleHelper from '../../../utilities/TriggerRuleHelper';
import classNames from 'classnames';
import { useZoneContext } from '../../../contexts/useZoneContext';
import { useZoneGetAssertionDescriptors } from '../../../shared/react-query-hooks/useZoneGetAssertionDescriptors';

const caseManagerMapper = (item: IQueryFilterCriteria) => {
    if (item.staticItemId === CaseFileStaticItems.CaseManager.Id) {
        return { ...item, label: 'A1/A2' };
    }
    return item;
};

export interface ICaseTriggerDetailsModalProps {
    currentAlert?: IAlertListModel;
    onClose: () => void;
    open: boolean;
    user: IUserModel;
}

export function CaseTriggerDetailsModal(props: ICaseTriggerDetailsModalProps) {
    const zoneContext = useZoneContext();

    if (!props.open || !props.currentAlert) return null;

    const getCaseTriggerCaseTitle = () => {
        const caseTitle: ICaseTitleModel = {};
        if (props.currentAlert) {
            caseTitle.caseName = props.currentAlert.originEntityName;
            caseTitle.priority = props.currentAlert.priority;
            caseTitle.SQScore = props.currentAlert.sqScore;
            caseTitle.SQProjection = props.currentAlert.sqProjection;
            caseTitle.guid = props.currentAlert.originEntityKey;
        }
        return caseTitle;
    };

    return (
        <Modal
            centered
            show={true}
            onHide={props.onClose}
            backdrop
            size="lg"
            scrollable={true}
        >
            <Modal.Header>
                <Modal.Title>
                    <CaseName
                        caseTitle={getCaseTriggerCaseTitle()}
                        openLinkInNewTab={true}
                        additionalText="- Case Trigger Details"
                        zoneContext={zoneContext}
                    />
                </Modal.Title>
                <button className="btn btn-no-bg float-end" onClick={props.onClose}>
                    <i className="fal fa-xmark-large" />
                </button>
            </Modal.Header>
            <Modal.Body>
                <CaseDataSection currentAlert={props.currentAlert} />
                <TriggerRulesDetailSection currentAlert={props.currentAlert} />
            </Modal.Body>
        </Modal>
    );
}

interface ITriggerRulesSectionProps {
    currentAlert?: IAlertListModel;
}

function TriggerRulesDetailSection(props: ITriggerRulesSectionProps) {
    if (!props.currentAlert?.caseTriggerDetail?.triggerRules) return null;

    const assertionDescriptors = useZoneGetAssertionDescriptors(
        AssertionDescriptorUsageTypesEnum.Case
    );

    const fieldsThatCausedAlert = props.currentAlert.caseTriggerDetail.fields.filter(
        (value: ICaseTriggerFieldModel) => value.didCauseAlert === true
    );

    return (
        <>
            <h5 className="mt-4">Trigger Rule Details</h5>

            <table className="table full-width mt-2">
                <thead>
                    <tr>
                        <th className="col-sm-4">Rule Name</th>
                        <th className="col-sm-4">Case Criteria</th>
                        <th className="col-sm-4">Trigger Field(s)</th>
                    </tr>
                </thead>
                <tbody>
                    {props.currentAlert.caseTriggerDetail.triggerRules.map(
                        (triggerRule: ITriggerRuleSettingsModel) => {
                            return (
                                <tr key={triggerRule.guid}>
                                    <td>{triggerRule.ruleName ?? ''}</td>
                                    <td>
                                        <table>
                                            <tbody>
                                                {triggerRule.filters
                                                    .map(caseManagerMapper)
                                                    .map(
                                                        (
                                                            item: IQueryFilterCriteria,
                                                            idx: number
                                                        ) => {
                                                            let fieldSummary = item.label + ': ';

                                                            if (item.isAssertionDescriptor) {
                                                                const assertionDescriptorMatch =
                                                                    assertionDescriptors.data?.find(
                                                                        (x: any) =>
                                                                            item.assertionDescriptorGuid !==
                                                                                undefined && item.assertionDescriptorGuid != null &&
                                                                            x.guid !== undefined && x.guid != null &&
                                                                            x.guid.toUpperCase() ===
                                                                                item.assertionDescriptorGuid.toUpperCase()
                                                                    );

                                                                if (assertionDescriptorMatch) {
                                                                    const summary =
                                                                        TriggerRuleHelper.getAssertionDescriptorSummary(
                                                                            item,
                                                                            assertionDescriptorMatch
                                                                        );
                                                                    fieldSummary += summary;
                                                                }
                                                            } else {
                                                                fieldSummary +=
                                                                    TriggerRuleHelper.getNonAssertionDescriptorSummary(
                                                                        item
                                                                    );
                                                            }
                                                            return (
                                                                <tr key={'_criteriaSum_' + idx}>
                                                                    <td>{fieldSummary}</td>
                                                                </tr>
                                                            );
                                                        }
                                                    )}
                                            </tbody>
                                        </table>
                                    </td>
                                    <td>
                                        <table>
                                            <tbody>
                                                {triggerRule.triggerFields.map(
                                                    (
                                                        triggerRuleField: ITriggerRuleField,
                                                        idx: number
                                                    ) => {
                                                        let doesFieldMatchAlert = undefined;
                                                        if (
                                                            triggerRuleField.isAssertionDescriptor
                                                        ) {
                                                            const matchedAssertionDescriptors =
                                                                fieldsThatCausedAlert.filter(
                                                                    (r: ICaseTriggerFieldModel) =>
                                                                        r.assertionDescriptorGuid ===
                                                                        triggerRuleField.assertionDescriptorGuid
                                                                );
                                                            const matchedRuleCausedAlerts =
                                                                matchedAssertionDescriptors?.filter(
                                                                    (x) =>
                                                                        x.rulesThatCausedAlert.includes(
                                                                            triggerRule.guid ?? ''
                                                                        )
                                                                );

                                                            if (
                                                                matchedRuleCausedAlerts.length > 0
                                                            ) {
                                                                doesFieldMatchAlert = true;
                                                            }
                                                        } else {
                                                            const matchedStaticItems =
                                                                fieldsThatCausedAlert.filter(
                                                                    (r: ICaseTriggerFieldModel) =>
                                                                        r.staticItemId ===
                                                                        triggerRuleField.staticItemId
                                                                );
                                                            const matchedStaticRuleCausedAlerts =
                                                                matchedStaticItems?.filter((x) =>
                                                                    x.rulesThatCausedAlert.includes(
                                                                        triggerRule.guid ?? ''
                                                                    )
                                                                );

                                                            if (
                                                                matchedStaticRuleCausedAlerts.length >
                                                                0
                                                            ) {
                                                                doesFieldMatchAlert = true;
                                                            }
                                                        }

                                                        const cssClass = classNames({
                                                            'font-orange':
                                                                doesFieldMatchAlert === true,
                                                        });

                                                        let fieldSummary =
                                                            triggerRuleField.label + ': ';

                                                        if (
                                                            triggerRuleField.isAssertionDescriptor
                                                        ) {
                                                            const assertionDescriptorMatch =
                                                                assertionDescriptors.data?.find(
                                                                    (x: any) =>
                                                                        triggerRuleField.assertionDescriptorGuid !==
                                                                            undefined && triggerRuleField.assertionDescriptorGuid != null &&
                                                                        x.guid !== undefined && x.guid != null &&
                                                                        x.guid.toUpperCase() ===
                                                                            triggerRuleField.assertionDescriptorGuid.toUpperCase()
                                                                );

                                                            if (assertionDescriptorMatch)
                                                                fieldSummary =
                                                                    TriggerRuleHelper.getTriggerAssertionDescriptorSummary(
                                                                        triggerRuleField,
                                                                        assertionDescriptorMatch
                                                                    );
                                                        } else {
                                                            fieldSummary =
                                                                TriggerRuleHelper.getTriggerNonAssertionDescriptorSummary(
                                                                    triggerRuleField
                                                                );
                                                        }

                                                        return (
                                                            <tr key={'_trigSummary_' + idx}>
                                                                <td className={cssClass}>
                                                                    {fieldSummary}
                                                                </td>
                                                            </tr>
                                                        );
                                                    }
                                                )}
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            );
                        }
                    )}
                </tbody>
            </table>
        </>
    );
}

export interface ICaseDataSectionProps {
    currentAlert?: IAlertListModel;
}

export function CaseDataSection(props: ICaseDataSectionProps) {
    if (!props.currentAlert?.caseTriggerDetail?.fields) return null;

    return (
        <>
            <h5>Case Data</h5>
            <table className="table full-width mt-2">
                <thead>
                    <tr>
                        <th className="col-sm-3">Name</th>
                        <th className="col-sm-3">Value</th>
                        <th className="col-sm-3">Previous Value</th>
                        <th className="col-sm-3">Rule Name</th>
                    </tr>
                </thead>
                <tbody>
                    {props.currentAlert.caseTriggerDetail.fields.map(
                        (item: ICaseTriggerFieldModel) => {
                            const cssClass = classNames({
                                'font-orange': item.value !== item.previousValue,
                            });
                            const rules = item.ruleName.split('|');
                            return (
                                <tr>
                                    <td>{item.name ?? ''}</td>
                                    <td className={cssClass}>
                                        {item.isCurrency
                                            ? Common.formatCurrency(item.value)
                                            : item.value}
                                    </td>
                                    <td className={cssClass}>
                                        {item.isCurrency
                                            ? Common.formatCurrency(item.previousValue)
                                            : item.previousValue}
                                    </td>
                                    <td>
                                        <table>
                                            <tbody>
                                                {rules.map((rule: any, idx: number) => {
                                                    return (
                                                        <tr key={'_ruleName_' + idx}>
                                                            <td>{rule}</td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </td>
                                </tr>
                            );
                        }
                    )}
                </tbody>
            </table>
        </>
    );
}
